import React, { useState } from 'react';
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from '@progress/kendo-react-layout';
import cn from 'clsx';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';
import {
    getSessionStorageActivePageTabLegacy,
    setSessionStorageActivePageTabLegacy,
} from '@utils-clean/page-tab-helpers';

export interface ICoreTab {
    tabTitle: React.ReactNode;
    tabContent: React.ReactNode;
    tabContentClassName?: string;
    disabled?: boolean;
    dataTestId: string;
}

interface CoreTabsProps {
    tabsData: ICoreTab[];
    className?: string;
    type?: 'page' | 'toolkit';
    bodyPadding?: 'default' | 'none';
    activeTab?: number;
    setActiveTab?: (t: number) => void;
    scrollable?: boolean;
    'data-testid': string;
}

export const CoreTabs = ({
    tabsData,
    className,
    type,
    bodyPadding = 'default',
    activeTab,
    setActiveTab,
    scrollable,
    'data-testid': mainDataTestId,
}: CoreTabsProps) => {
    const [active, setActive] = useState(0);
    const { readOnly } = useReadOnlyContext();

    const handleTabChange = (e: TabStripSelectEventArguments) => {
        if (setActiveTab) {
            setActiveTab(e.selected);
        } else {
            setActive(e.selected);
        }
    };

    return (
        <div data-testid={mainDataTestId} className="core-tabs">
            <TabStrip
                selected={activeTab ?? active}
                onSelect={handleTabChange}
                className={cn(className, {
                    'tabstrip-pages': type === 'page',
                    'no-padding': bodyPadding === 'none',
                    'read-only-context': readOnly,
                })}
                scrollable={scrollable}
            >
                {tabsData.map(({ tabTitle, tabContent, disabled, dataTestId, tabContentClassName }) => (
                    <TabStripTab
                        key={dataTestId}
                        title={<div data-testid={`tab-${dataTestId}`}>{tabTitle}</div>}
                        disabled={disabled}
                    >
                        <div className={tabContentClassName} data-testid={dataTestId}>
                            {tabContent}
                        </div>
                    </TabStripTab>
                ))}
            </TabStrip>
        </div>
    );
};

export const PageTabs = ({
    tabsData,
    className,
    type,
    bodyPadding = 'default',
    scrollable,
    'data-testid': mainDataTestId,
}: Omit<CoreTabsProps, 'activeTab' | 'setActiveTab'>) => {
    const [activePageTab, setActivePageTab] = useState<number>(getSessionStorageActivePageTabLegacy());

    const handleTabChange = (tab: number) => {
        setActivePageTab(tab);
        setSessionStorageActivePageTabLegacy(tab);
    };

    return (
        <CoreTabs
            tabsData={tabsData}
            className={className}
            type={type}
            bodyPadding={bodyPadding}
            activeTab={activePageTab}
            setActiveTab={handleTabChange}
            scrollable={scrollable}
            data-testid={mainDataTestId}
        />
    );
};
